import React, { useEffect, useState } from 'react';

import { useToggle } from 'react-use';
import { Button, Divider, InfoBox } from '@infoods/lifebase-ui';

import classes from './PaymentDetailsWidget.module.scss';
import { CardDetails } from '../../../../../components/CardDetails/CardDetails';
import { UpdateCardModal } from './UpdateCardModal/UpdateCardModal';

import { TitledInfo } from '../../../../../../shared/components/ui-components';
import {
  GridTableCell,
  GridTableRow,
} from '../../../../../../shared/components/table-components';
import { EntityPaymentMethodDetails } from '../../../../../../shared/interfaces/entityPaymentMethodDetails';
import { getHumanizeBillingPeriodType } from '../../../../../utils/getHumanizeBillingPeriodType/getHumanizeBillingPeriodType';
import { getFormattedLocalDate } from '../../../../../../shared/utils/dateUtils';
import { localDataFormat } from '../../../../../../shared/constants/apiDateFormat';
import { entityPaymentService } from '../../../../../../shared/services/entity/entityPaymentService';

type PaymentDetailsWidgetProps = {
  entityId: number;
};

const PaymentDetailsWidget = ({ entityId }: PaymentDetailsWidgetProps) => {
  const [isUpdateCardModalVisible, toggleUpdateCardModalVisible] = useToggle(false);
  const [paymentMethodDetails, setPaymentMethodDetails] =
    useState<EntityPaymentMethodDetails>();

  const getPaymentMethodDetails = async () => {
    const response = await entityPaymentService.getEntityPaymentMethodDetails(entityId, {
      ignoreErrorCodes: [404],
    });

    setPaymentMethodDetails(response);
  };

  useEffect(() => {
    getPaymentMethodDetails();
  }, [entityId]);

  const formattedActiveSinceDate = paymentMethodDetails
    ? getFormattedLocalDate(
        paymentMethodDetails.billingInfo.activeSinceDateTime,
        localDataFormat,
      )
    : null;

  const billingPeriodType = paymentMethodDetails
    ? getHumanizeBillingPeriodType(paymentMethodDetails.billingInfo.billingPeriodType)
    : null;

  return (
    <>
      <InfoBox title="Payment Details" customContentPadding>
        <div className={classes.paymentDetailsTableView}>
          <GridTableRow variant="unstyled" className={classes.cardInfoRow}>
            <GridTableCell variant="title">Payment Card</GridTableCell>
            <GridTableCell>
              <CardDetails paymentMethodDetails={paymentMethodDetails} />
            </GridTableCell>
          </GridTableRow>
          <Divider className={classes.divider} />
          <GridTableRow variant="unstyled" className={classes.billingInfoRow}>
            <GridTableCell>
              <TitledInfo title="Billing Period">
                {billingPeriodType && (
                  <div className="private-info">{billingPeriodType}</div>
                )}
              </TitledInfo>
            </GridTableCell>
            <GridTableCell>
              <TitledInfo title="Active Since">
                {formattedActiveSinceDate && (
                  <div className="private-info">{formattedActiveSinceDate}</div>
                )}
              </TitledInfo>
            </GridTableCell>
          </GridTableRow>
          <GridTableRow variant="unstyled">
            <GridTableCell>
              <Button
                variant="contained"
                size="small"
                disabled={!paymentMethodDetails}
                onClick={toggleUpdateCardModalVisible}
              >
                Update Card
              </Button>
            </GridTableCell>
          </GridTableRow>
        </div>
      </InfoBox>
      {isUpdateCardModalVisible && (
        <UpdateCardModal
          entityId={entityId}
          onSuccessSubmit={setPaymentMethodDetails}
          onClose={toggleUpdateCardModalVisible}
        />
      )}
    </>
  );
};

export { PaymentDetailsWidget };
